import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactNode, useLayoutEffect, useMemo, useState } from 'react';

import { ProductDetail } from '@api/catalog';
import { SeoData } from '@api/seo/types';

import { useSeoContext } from '@context/seo';

import { getSiteOrigin } from '@utils/config';

import LoadWrapper from '@components/controls/LoadWrapper';
import NotFoundPage from '@components/NotFoundPage/NotFoundPage';

import { toAbsoluteUrl } from '@scripts/seo/toAbsoluteUrl';

import { isCmsSelfCanonical } from './isCmsSelfCanonical';
import { SeoHead } from './SeoHead';

const AuthPopup = dynamic(() => import('@components/NewAuth'), { ssr: false });

const parseQueryPage = (page: string | string[] | undefined): number => {
    const raw = Array.isArray(page) ? page[0] : page;
    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

type PageWrapperProps = {
    h1?: string;
    title?: string;
    error?: string;
    isLoading?: boolean;
    children: ReactNode;
    className?: string;
    seoProps?: Partial<SeoData>;
    productData?: ProductDetail;
    canonicalUrl?: string;
    isNotFound?: boolean;
    isError?: boolean;
    dataCategoryId?: null | number;
    dataOrderId?: string;
    dataCost?: number;
    dataOfferId?: null | number;
};

const PageWrapper = ({
    isLoading = false,
    error,
    children,
    className,
    productData,
    canonicalUrl,
    seoProps,
    isNotFound = false,
    isError = false,
    dataCategoryId,
    dataOrderId,
    dataCost,
    dataOfferId,
}: PageWrapperProps) => {
    const renderErrorPage = () => {
        if (isError) return <NotFoundPage isNotFound={false} />;
        if (isNotFound) return <NotFoundPage />;

        return children;
    };

    const { query, asPath, pathname } = useRouter();

    useLayoutEffect(() => {
        if (!isNotFound && !isError) {
            return undefined;
        }

        document.body.classList.add('grey-content-page');

        return () => {
            document.body.classList.remove('grey-content-page');
        };
    }, [isNotFound, isError]);

    const seoContextData = useSeoContext();
    const frontendHost = getSiteOrigin();

    const [isAuthPopup, setIsAuthPopup] = useState(false);

    const toAbsoluteCanonical = (value: string) => {
        if (!value || !frontendHost) {
            return value;
        }

        return toAbsoluteUrl(frontendHost, value);
    };

    const currentRoute = useMemo(() => {
        const path = asPath.indexOf('?') === -1 ? asPath : asPath.split('?')[0];

        return frontendHost ? toAbsoluteUrl(frontendHost, path) : path;
    }, [asPath, frontendHost]);

    const fullCurrentRoute = useMemo(() => {
        const [path, queryString] = asPath.split('?');
        const absolutePath = frontendHost ? toAbsoluteUrl(frontendHost, path) : path;

        if (!queryString) {
            return absolutePath;
        }

        const params = new URLSearchParams(queryString);
        const pageParam = params.get('page');

        if (pageParam) {
            return `${absolutePath}?page=${pageParam}`;
        }

        return absolutePath;
    }, [asPath, frontendHost]);

    const resolveCanonical = (value: string) => {
        if (isCmsSelfCanonical(value, frontendHost)) {
            return currentRoute;
        }

        return toAbsoluteCanonical(value);
    };

    const activePageFromQuery = parseQueryPage(query.page);

    const canonicalGenerating = () => {
        switch (true) {
            case pathname === '/product/[id]':
                return fullCurrentRoute;
            case pathname === '/brands':
                return currentRoute;
            case pathname === '/brands/[brand]':
                return fullCurrentRoute;
            case pathname === '/catalog/[category]':
                return fullCurrentRoute;
            case pathname === '/catalog/collections/[code]':
                return fullCurrentRoute;
            case pathname === '/news/[slug]':
            case pathname === '/news':
                return currentRoute;
            case !!seoContextData.canonical:
                return resolveCanonical(seoContextData.canonical!);
            default:
                return currentRoute;
        }
    };

    const seo = isNotFound
        ? {
              title: 'Страница не найдена',
              description: 'Запрашиваемая страница не существует или была удалена.',
              canonical: canonicalGenerating(),
              nameDigi: 'error',
              contentDigi: 'Digi 404 Page',
          }
        : isError
          ? {
                title: 'Что-то пошло не так',
                description: 'Что-то пошло не так',
                canonical: canonicalGenerating(),
                nameDigi: 'error',
                contentDigi: 'Digi 404 Page',
            }
          : {
                ...seoContextData,
                ...seoProps,
                description: seoProps?.description || seoContextData.description,
                title: seoProps?.title || seoContextData.title,
                image: seoProps?.image || seoContextData.image,
                canonical: seoProps?.canonical ? resolveCanonical(seoProps.canonical) : canonicalGenerating(),
                productData: productData,
                canonicalUrl: canonicalUrl,
                activePage: seoProps?.activePage ?? activePageFromQuery,
                totalPages: seoProps?.totalPages,
            };

    return (
        <div
            css={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                ...((isNotFound || isError) && {
                    backgroundColor: 'var(--color-lightBackground)',
                }),
            }}
        >
            <SeoHead {...seo} />
            <LoadWrapper isLoading={isLoading} error={error}>
                <main
                    css={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0,
                        flex: 1,
                        ...((isNotFound || isError) && {
                            backgroundColor: 'var(--color-lightBackground)',
                        }),
                    }}
                    className={cn(
                        className,
                        dataCategoryId != null && 'any-category-id',
                        dataOrderId != null && 'any-order-id',
                        dataCost != null && 'any-total',
                        dataOfferId != null && 'any-offer-id'
                    )}
                    {...(dataCategoryId != null
                        ? { 'data-categoryid': dataCategoryId, 'data-any-category-id': dataCategoryId }
                        : {})}
                    {...(dataOrderId != null ? { 'data-orderid': dataOrderId, 'data-any-order-id': dataOrderId } : {})}
                    {...(dataCost != null ? { 'data-cost': dataCost, 'data-any-total': dataCost } : {})}
                    {...(dataOfferId != null ? { 'data-any-offer-id': dataOfferId } : {})}
                >
                    {renderErrorPage()}
                </main>
            </LoadWrapper>
            <AuthPopup isRegistration={true} isOpen={isAuthPopup} closeHandler={() => setIsAuthPopup(false)} />
        </div>
    );
};

export default PageWrapper;
